import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { useEnquiry } from "../enquiry-context";
import { CONTACT } from "../data";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Process", href: "#process" },
  { label: "Machinery", href: "#machinery" },
  { label: "Products", href: "#products" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

export function scrollToSection(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Header() {
  const { openEnquiry } = useEnquiry();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        scrolled
          ? "bg-[#FAF8F3]/90 backdrop-blur-xl border-stone-200 shadow-[0_8px_30px_rgba(20,23,28,0.06)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="nav-brand-logo"
          onClick={(e) => go(e, "#top")}
          className="flex items-center shrink-0"
        >
          <Logo height={54} variant={scrolled ? "dark" : "white"} />
          <span data-testid="nav-tagline" className="sr-only">
            {CONTACT.tagline}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={(e) => go(e, l.href)}
              className={`text-[13px] uppercase tracking-[0.14em] font-tech font-medium transition-colors ${
                scrolled
                  ? "text-stone-700 hover:text-amber-700"
                  : "text-stone-100 hover:text-amber-400"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-get-quote-button"
            onClick={() => openEnquiry("General Enquiry")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-[13px] font-bold uppercase tracking-wider text-stone-900 hover:bg-amber-400 hover:shadow-[0_10px_28px_rgba(217,160,43,0.35)] transition-all"
          >
            Get Quote <ArrowUpRight size={15} strokeWidth={2.5} />
          </button>
          <button
            className={`lg:hidden p-2 transition-colors ${
              scrolled ? "text-stone-800" : "text-stone-100"
            }`}
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-stone-200 bg-[#FAF8F3]/97 backdrop-blur-xl px-5 py-4 shadow-[0_20px_40px_rgba(20,23,28,0.08)]">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              className="block py-3 font-tech text-sm uppercase tracking-[0.14em] text-stone-800 border-b border-stone-200 last:border-0"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setMenuOpen(false);
              openEnquiry("General Enquiry");
            }}
            className="mt-4 w-full rounded-full bg-amber-500 px-5 py-3 text-sm font-bold uppercase tracking-wider text-stone-900"
            data-testid="nav-mobile-quote-button"
          >
            Get Quote
          </button>
        </div>
      )}
    </header>
  );
}
